import { resolveTemplateSource } from "./template-slot-card";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/** Where one generated document comes from and where it is filed. */
export type SlotValues = {
  source: "system" | "custom";
  templateId: string;
  seriesId: string;
  fileTypeId: string;
  categoryId: string;
};

/** The names of the company fields that hold the SlotValues of one document. */
export type SlotFieldNames = {
  template: string;
  source: string;
  series: string;
  fileType: string;
  category: string;
};

/** Titles looked up by id, to say in words what a saved SlotValues points at. */
export type SlotNames = {
  templateNameById: Record<string, string>;
  categoryPathById: Record<string, string>;
};

export function readSlotValues(
  company: any,
  fields: SlotFieldNames
): SlotValues {
  return {
    source: resolveTemplateSource(
      company[fields.source],
      company[fields.template]
    ),
    templateId: company[fields.template] || "",
    seriesId: company[fields.series] || "",
    fileTypeId: company[fields.fileType] || "",
    categoryId: company[fields.category] || "",
  };
}

/** The body fields the settings endpoints expect for one document. */
export function slotPayload(fields: SlotFieldNames, values: SlotValues) {
  return {
    [fields.template]: values.templateId,
    [fields.source]: values.source,
    [fields.series]: values.seriesId,
    [fields.fileType]: values.fileTypeId,
    [fields.category]: values.categoryId,
  };
}

/** What the server insists on: a custom template needs the template, a system one series, file type and category. */
export function isSlotComplete(values: SlotValues): boolean {
  if (values.source === "custom") return Boolean(values.templateId);
  return Boolean(values.seriesId && values.fileTypeId && values.categoryId);
}

/** "System template · placed in: HR › Business trips" */
export function describeSlot(
  values: SlotValues,
  names: SlotNames,
  t: TFunction
): string {
  const template =
    values.source === "custom"
      ? names.templateNameById[values.templateId] ?? t("template_slot_custom")
      : t("template_slot_system");

  if (!values.categoryId) {
    return `${template} · ${t("template_slot_not_placed")}`;
  }
  const category = names.categoryPathById[values.categoryId];
  return category
    ? `${template} · ${t("template_slot_placed_in", { category })}`
    : template;
}

/** The server refuses a save with a plain code in the response body; this says it in words. */
export function slotSaveErrorMessage(err: unknown, t: TFunction): string {
  const code = (err as { response?: { data?: unknown } }).response?.data;
  if (typeof code !== "string") return t("settings_save_failed");

  if (code.endsWith("_SYSTEM_METADATA_REQUIRED")) {
    return t("template_slot_system_metadata_required");
  }
  if (code.startsWith("MISSING_") && code.endsWith("_TEMPLATE")) {
    return t("template_slot_template_required");
  }
  if (/_(TEMPLATE|SERIES|FILE_TYPE|CATEGORY)_NOT_FOUND$/.test(code)) {
    return t("template_slot_choice_missing");
  }
  return t("settings_save_failed");
}
